import { Query } from '../types/tileBuilderTypes';
import { fetchQueryForPreview } from './queryHelpers';
import { evaluateResultMapping } from './customTileTransformUtils';
import { getLocalJsonFileByPath } from './localFileRegistry';
import { isFileImportQuery, resolveFileImportData } from './dataImport/fileImportSource';

interface QueryExecutionContext {
  queries: Query[];
  availableVariables: Record<string, string>;
  skipIfEmptyMapping?: boolean;
}

interface QueryResult {
  queryIndex: number;
  rawResult: any;
  transformedResult: any;
  error?: string;
}

/**
 * Execute queries in dependency order with chaining support
 */
export async function executeQueriesWithChaining(
  context: QueryExecutionContext
): Promise<QueryResult[]> {
  const { queries, availableVariables } = context;
  const results: QueryResult[] = [];
  const executionOrder = determineExecutionOrder(queries);

  for (const queryIndex of executionOrder) {
    const query = queries[queryIndex];

    try {
      const preparedQuery = await prepareQueryWithChainedResults(
        query,
        queryIndex,
        results,
        availableVariables
      );

      let rawResult: any;

      if (isFileImportQuery(preparedQuery)) {
        rawResult = await resolveFileImportData(preparedQuery);
      } else if (preparedQuery.dataSource === 'Local File') {
        if (!preparedQuery.localFilePath) {
          throw new Error(`Query ${queryIndex + 1}: no local file selected`);
        }

        const file = getLocalJsonFileByPath(preparedQuery.localFilePath);

        if (!file) {
          throw new Error(
            `Query ${queryIndex + 1}: local file not found: ${preparedQuery.localFilePath}`
          );
        }

        rawResult = file.data;
      } else {
        // Debug aid: confirms the executor is passing the expected request shape
        /*
        console.log('executeQueriesWithChaining preparedQuery', {
          queryIndex,
          dataSource: preparedQuery.dataSource,
          model: preparedQuery.model,
          function: preparedQuery.function,
          path: preparedQuery.path,
          method: preparedQuery.method,
          params: preparedQuery.params,
          payload: preparedQuery.payload,
          current_time_filter: preparedQuery.params?.current_time_filter,
        });
        */

        rawResult = await fetchQueryForPreview(preparedQuery);
      }

      const transformedResult = query.transformFunction
        ? applyTransformation(rawResult, query.transformFunction)
        : rawResult;

      results.push({
        queryIndex,
        rawResult,
        transformedResult,
      });
    } catch (error: any) {
      console.error(`Query ${queryIndex} execution failed:`, error);
      results.push({
        queryIndex,
        rawResult: null,
        transformedResult: null,
        error: error.message,
      });
    }
  }

  return results;
}

/**
 * Determine the order in which queries should execute based on dependencies
 */
function determineExecutionOrder(queries: Query[]): number[] {
  const order: number[] = [];
  const visited = new Set<number>();
  const visiting = new Set<number>();

  function visit(index: number) {
    if (visited.has(index)) return;
    if (visiting.has(index)) {
      throw new Error(`Circular dependency detected at query ${index}`);
    }

    visiting.add(index);

    const query = queries[index];
    const dependencies = [
      ...(query.dependsOn || []),
      ...(query.useResultFrom !== undefined && query.useResultFrom !== null
        ? [query.useResultFrom]
        : []),
    ];

    for (const depIndex of dependencies) {
      if (depIndex >= 0 && depIndex < queries.length) {
        visit(depIndex);
      }
    }

    visiting.delete(index);
    visited.add(index);
    order.push(index);
  }

  for (let i = 0; i < queries.length; i++) {
    visit(i);
  }

  return order;
}

/**
 * Prepare query by injecting results from previous queries
 */
async function prepareQueryWithChainedResults(
  query: Query,
  queryIndex: number,
  completedResults: QueryResult[],
  availableVariables: Record<string, string>
): Promise<Query> {
  const preparedQuery: Query = JSON.parse(JSON.stringify(query));

  let mergedVariables = { ...availableVariables };

  if (query.useResultFrom !== undefined && query.useResultFrom !== null) {
    const sourceResult = completedResults.find(
      (r) => r.queryIndex === query.useResultFrom
    );

    if (!sourceResult) {
      throw new Error(
        `Query ${queryIndex} depends on query ${query.useResultFrom} which hasn't been executed yet`
      );
    }

    const sourceData = sourceResult.transformedResult || sourceResult.rawResult;

    if (query.resultMapping && Object.keys(query.resultMapping).length > 0) {
      const evaluated = evaluateResultMapping(query.resultMapping, sourceData);
      mergedVariables = { ...mergedVariables, ...evaluated };
    } else {
      mergedVariables[`query${query.useResultFrom}Result`] = JSON.stringify(sourceData);
    }
  }

  if (preparedQuery.dataSource === 'Local File') {
    return preparedQuery;
  }

  preparedQuery.path = substituteVariables(preparedQuery.path || '', mergedVariables);

  const resolvedParams = Object.fromEntries(
    Object.entries(preparedQuery.params || {}).map(([k, v]) => [
      k,
      substituteVariables(String(v), mergedVariables),
    ])
  );

  // Ensure current_time_filter is preserved for preview/edit-transformation execution. A query flagged
  // `enableDateFilter` is driven by the selected filter, so it wins over a value saved in its params.
  if (
    mergedVariables.current_time_filter != null &&
    mergedVariables.current_time_filter !== '' &&
    (resolvedParams.current_time_filter == null || query.enableDateFilter)
  ) {
    resolvedParams.current_time_filter = String(mergedVariables.current_time_filter);
  }

  preparedQuery.params = resolvedParams;

  if (preparedQuery.payload && typeof preparedQuery.payload === 'string') {
    preparedQuery.payload = substituteVariables(preparedQuery.payload, mergedVariables);
  }

  return preparedQuery;
}

/**
 * Apply transformation function to query result
 */
function applyTransformation(data: any, transformFunction: string): any {
  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function('data', transformFunction);
    return fn(data);
  } catch (error: any) {
    console.error('Transformation function error:', error);
    throw new Error(`Transform failed: ${error.message}`);
  }
}

/**
 * Get nested value from object using dot notation
 */
function getNestedValue(obj: any, path: string): any {
  const keys = path.split('.');
  let value = obj;

  for (const key of keys) {
    if (value === null || value === undefined) {
      return undefined;
    }
    value = value[key];
  }

  return value;
}

/**
 * Set nested value in object using dot notation
 */
function setNestedValue(obj: any, path: string, value: any): void {
  const keys = path.split('.');
  const lastKey = keys.pop()!;
  let current = obj;

  for (const key of keys) {
    if (!(key in current) || typeof current[key] !== 'object') {
      current[key] = {};
    }
    current = current[key];
  }

  current[lastKey] = value;
}

/**
 * Substitute variables in template string
 */
function substituteVariables(
  template: string,
  vars: Record<string, string>
): string {
  return template.replace(/\{(\w+)\}/g, (_, key) =>
    vars[key] != null ? String(vars[key]) : `{${key}}`
  );
}